import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthToken } from "../State/Hooks/authHooks";
import { useAppSelector } from "../State/Hooks/reduxHooks";
import { selectClientValue } from "../State/Slices/SelectedClientSlice";
import { useGroupCandidatesFilterStore } from "../State/Stores/GroupCandidatesPaginationStore";
import { ICandidate } from "../Typings/Candidate";
import {
  IAddMembersToGroup,
  IAddMembersToGroupCommand,
  IArchivedGroup,
  IGroup,
  IGroupAttachmentsExportRequest,
  IGroupExportRequest,
  IMoveGroupMembers,
  IMoveGroupMembersCommand,
  IRemoveGroupMember,
  ISaveGroup,
  ISaveGroupCommand,
} from "../Typings/Groups";
import { IPagination } from "../Typings/MessageText";
import { ISuccessResponse } from "../Typings/Response";
import { parseCandidate } from "./CandidateQueries";
import { apiPathGroup, fetcher, secureFetcher } from "./queryHelpers";
import { useCurrentUser } from "./UserQueries";

/**
 * Gets list of groups for the currently selected client
 *
 * @returns UseQueryResult<IGroup[]>
 */
export const useGroupsQuery = <T = IPagination<IGroup>>(
  includeArchived = false,
  select?: (data: IPagination<IGroup>) => T | undefined,
) => {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  let path = apiPathGroup + "client/" + clientId;
  if (includeArchived) {
    path += "?includeArchived=true";
  }
  return useQuery(["groups", { clientId, includeArchived }], () => secureFetcher(path, token), {
    enabled: !!clientId && !!token,
    select,
    staleTime: 60 * 5 * 1000, // 5 minutes
  });
};

/**
 * Gets list of archived groups for the currently selected client
 *
 * @returns UseQueryResult<IArchivedGroup[]>
 */
export const useArchivedGroupsQuery = (queryString: string, enabled = false) => {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  const path = `${apiPathGroup}client/${clientId}/archive${queryString}`;
  return useQuery<IPagination<IArchivedGroup>>(
    ["archivedGroups", { clientId, queryString }],
    () => secureFetcher(path, token),
    {
      enabled: enabled && !!clientId && !!token,
      staleTime: 60 * 5 * 1000, // 5 minutes
    },
  );
};

/**
 * Gets list of groups for the currently selected client
 *
 * @returns UseQueryResult<ICandidate[]>
 */
export const useGroupDynamicMembersQuery = (data: string, campaignId?: string, query?: string) => {
  const { clientId } = useAppSelector(selectClientValue);
  const token = useAuthToken();

  return useQuery<IPagination<ICandidate>>(
    ["groupDynamicMembers", clientId, campaignId, query],
    async () => {
      return await secureFetcher(
        `${apiPathGroup}client/${clientId}/candidates/preview${query ?? ""}`,
        token,
        {
          body: JSON.stringify({ clientId: clientId, dynamicGroupCampaignId: campaignId, rulesJson: data }),
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
    },
    {
      enabled: !!clientId && !!token && !!data,
    },
  );
};

/**
 * Provides a way to select a subset of the groups for the currently selected client
 * @param select - Transform or select a part of the data returned from the original query
 * @param key - The key to use for the new query
 * @typeParam T - The type of the data returned from your select function
 * @returns UseQueryResult<IGroup[]>
 */
export function useGroupsQuerySelect<T>(select: (data: IPagination<IGroup>) => T | undefined) {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  return useQuery<IPagination<IGroup>, unknown, T | undefined>(
    ["groups", clientId],
    () => secureFetcher(apiPathGroup + "client/" + clientId, token),
    {
      select,
      enabled: !!clientId && !!token,
    },
  );
}

export const useCampaignGroup = (campaignId: string) => {
  return useGroupsQuerySelect((data) => data.items.find((group) => group.campaignId === campaignId));
};

/**
 * Used to quickly decide if a Group should be shown in the UI
 * @returns an array of Group IDs associated with the currently selected client
 */
export function useGroupIdsAvailable() {
  return useGroupsQuerySelect<string[]>((data) => data.items.map((c) => c.id));
}

export const useFilteredGroups = (queryString: string) => {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  return useQuery<IPagination<IGroup>>(
    ["groups", clientId, queryString],
    () => secureFetcher(apiPathGroup + "client/" + clientId + queryString, token),
    {
      enabled: !!clientId && !!token,
      staleTime: 60 * 5 * 1000, // 5 minutes
    },
  );
};

/**
 * Gets a single group based on the group id
 *
 * @returns UseQueryResult<IGroup[]>
 */
export const useGroupProfileQuery = <T = IGroup | undefined>(
  groupId: string,
  select?: (groups: IGroup) => T,
) => {
  const { clientId = "" } = useAppSelector(selectClientValue);
  const token = useAuthToken();
  return useQuery<IGroup, unknown, T>(
    ["groupProfile", clientId, groupId],
    () => secureFetcher(`${apiPathGroup}${clientId}/${groupId}`, token),
    {
      select,
      enabled: !!clientId && !!groupId && !!token,
      staleTime: 60 * 5 * 1000, // 5 minutes
    },
  );
};

export interface IGroupMembersPagination {
  ids: string[];
  pagination: IPagination<ICandidate>;
}

export const useGroupMembersQuery = (groupId: string, query?: string, enabled = true) => {
  const { clientId = "" } = useAppSelector(selectClientValue);
  const token = useAuthToken();
  return useQuery<IPagination<ICandidate>>(
    ["groupMembers", clientId, groupId, query],
    async () => {
      const response = (await secureFetcher(
        `${apiPathGroup}${clientId}/${groupId}/members${query}`,
        token,
      )) as IPagination<ICandidate>;
      return {
        ...response,
        items: response.items.map((item: ICandidate) => parseCandidate(item)),
      };
    },
    {
      enabled: enabled && !!clientId && !!groupId && !!token,
      staleTime: 60 * 5 * 1000, // 5 minutes
    },
  );
};

export function useFilteredGroupMembersPagingQuery(groupId: string, enabled = true) {
  const { page, filterQueryString, page2FilterQueryString } = useGroupCandidatesFilterStore(groupId);
  const { data: filteredCandidateList, isLoading: filteredCandidateListLoading } = useGroupMembersQuery(
    groupId,
    filterQueryString,
    enabled,
  );
  const shouldFetchNextPage = enabled && filteredCandidateList && filteredCandidateList.totalPages > page + 1;
  useGroupMembersQuery(groupId, page2FilterQueryString ?? "", shouldFetchNextPage);
  return {
    filteredCandidateList: filteredCandidateList?.items ?? [],
    totalItems: filteredCandidateList?.totalItems ?? 0,
    isLoading: filteredCandidateListLoading,
    filterQueryString,
  };
}

export const useGroupMemberIdsQuery = (groupId: string, filterQueryString: string, enabled = true) => {
  const { clientId = "" } = useAppSelector(selectClientValue);
  const token = useAuthToken();
  return useQuery<string[]>(
    ["groupMemberIds", clientId, groupId, filterQueryString],
    () => secureFetcher(`${apiPathGroup}${clientId}/${groupId}/memberIds${filterQueryString}`, token),
    {
      enabled: enabled && !!clientId && !!groupId && !!token,
      staleTime: 60 * 5 * 1000, // 5 minutes
    },
  );
};

/**
 * Deletes group based on a given group id
 *
 * Called using [variable].mutate(arg: groupId)
 *
 * @returns UseQueryResult<IGroup[]>
 */
export const useDeleteGroupMutation = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  return useMutation<boolean, unknown, string>(
    (groupId) =>
      fetcher(apiPathGroup + "client/" + clientId + "/group/" + groupId, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    {
      onSuccess: (_, groupId) => {
        queryClient.removeQueries(["groupProfile", clientId, groupId]);
        return queryClient.invalidateQueries(["groups", clientId]);
      },
      mutationKey: ["deleteGroup"],
    },
  );
};

/**
 * Saves a new or existing group with the given criteria
 *
 * Called using [variable].mutate(arg: ISaveGroupBody)
 *
 * @returns UseQueryResult<IGroup[]>
 */
export const useSaveGroupMutation = (groupId: string) => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  const { id: userId } = useCurrentUser();
  return useMutation<IGroup, unknown, ISaveGroup>(
    (saveGroupData) => {
      // if creating a new Group, we don't have an Id to pass in
      if (saveGroupData.id) {
        saveGroupData.id = groupId === "" ? saveGroupData.id : groupId;
      }

      const saveGroupCommand: ISaveGroupCommand = {
        ...saveGroupData,
        creatorId: userId,
        clientId,
      };

      return fetcher(apiPathGroup + "save", {
        method: "POST",
        body: JSON.stringify(saveGroupCommand),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onSuccess: (_response, saveGroupData) => {
        queryClient.invalidateQueries(["groups"]);
        queryClient.invalidateQueries(["groupProfile", clientId, saveGroupData.id ?? groupId]);
        queryClient.invalidateQueries(["groupMembers", clientId, saveGroupData.id ?? groupId]);
        queryClient.invalidateQueries(["groupMemberIds", clientId, saveGroupData.id ?? groupId]);
      },
      mutationKey: ["saveGroup"],
    },
  );
};

/**
 * Adds candidates to a group. If they already are added, it will not add them.
 * @returns
 */
export const useAddMembersToGroupMutation = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  const { id: userId } = useCurrentUser();
  return useMutation<ISuccessResponse, unknown, IAddMembersToGroup>(
    (addMembersToGroup) => {
      const addMembersCommand: IAddMembersToGroupCommand = {
        ...addMembersToGroup,
        clientId,
        candidateIds: addMembersToGroup.candidateIds,
        addedById: userId,
        groupId: addMembersToGroup.groupId,
      };

      return secureFetcher(apiPathGroup + `${addMembersToGroup.groupId}/members`, token, {
        method: "POST",
        body: JSON.stringify(addMembersCommand),
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    {
      onSuccess: (_, command) => {
        queryClient.invalidateQueries(["groups"]);
        queryClient.invalidateQueries(["groupProfile", clientId, command.groupId]);
        queryClient.invalidateQueries(["groupMembers", clientId, command.groupId]);
        queryClient.invalidateQueries(["groupMemberIds", clientId, command.groupId]);
      },
      mutationKey: ["addMembersToGroup"],
    },
  );
};

/**
 * Adds candidates to a group. If they already are added, it will not add them.
 * @returns
 */
export const useMoveGroupMembersMutation = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  const { id: userId } = useCurrentUser();
  return useMutation<ISuccessResponse, unknown, IMoveGroupMembers>(
    (moveGroupMembers) => {
      const moveGroupMembersCommand: IMoveGroupMembersCommand = {
        ...moveGroupMembers,
        clientId,
        userId,
      };

      return fetcher(apiPathGroup + "movecandidates", {
        method: "POST",
        body: JSON.stringify(moveGroupMembersCommand),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onSettled: (response, error, { groupId, targetGroupId }) => {
        if (!response || error) {
          console.error("Error moving group members", error);
          return;
        }
        if (!response.success) {
          console.error("Error moving group members", response.message);
          return;
        }
        queryClient.invalidateQueries(["groups", clientId]);
        queryClient.invalidateQueries(["groups", { clientId, includeArchived: false }]);
        queryClient.invalidateQueries(["groupProfile", clientId, targetGroupId]);
        queryClient.invalidateQueries(["groupMembers", clientId, targetGroupId]);
        queryClient.invalidateQueries(["groupMemberIds", clientId, targetGroupId]);
        queryClient.invalidateQueries(["groupMembers", clientId, groupId]);
        queryClient.invalidateQueries(["groupMemberIds", clientId, groupId]);
        return queryClient.invalidateQueries(["groupProfile", clientId, groupId]);
      },
      mutationKey: ["addMembersToGroup"],
    },
  );
};

/**
 * Removes a member from a group based on a given group id and candidate id
 *
 * Called using [variable].mutate(arg: IRemoveMemberBody)
 *
 * @returns UseMutationResult<boolean, unknown, IRemoveMemberBody, unknown>
 */
export const useRemoveGroupMemberMutation = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  return useMutation<boolean, unknown, IRemoveGroupMember>(
    (removeMemberDetails) => {
      const { groupId, candidateId } = removeMemberDetails;
      return fetcher(
        apiPathGroup + "client/" + clientId + "/group/" + groupId + "/candidate/" + candidateId,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    },
    {
      onSuccess: (response, removeMemberDetails) => {
        queryClient.invalidateQueries(["groups", clientId]);
        queryClient.invalidateQueries(["groupProfile", clientId, removeMemberDetails.groupId]);
        queryClient.invalidateQueries(["groupMembers", clientId, removeMemberDetails.groupId]);
        queryClient.invalidateQueries(["groupMemberIds", clientId, removeMemberDetails.groupId]);
      },
      mutationKey: ["removeGroupMember"],
    },
  );
};

export const useArchiveGroupsMutation = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  const { id: userId } = useCurrentUser();
  return useMutation<boolean, unknown, string[]>(
    (groupIds) => {
      const path = `${apiPathGroup}client/${clientId}/archive?groupIds=${groupIds.join(",")}&userId=${userId}`;
      return fetcher(path, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["groups", clientId]);
        queryClient.invalidateQueries(["groupProfile"]);
        return queryClient.invalidateQueries(["archivedGroups"]);
      },
      mutationKey: ["archiveGroup"],
    },
  );
};

export const useUnarchiveGroupsMutation = () => {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);
  return useMutation<boolean, unknown, string[]>(
    (groupIds) => {
      const path = `${apiPathGroup}client/${clientId}/unarchive?groupIds=${groupIds.join(",")}`;
      return fetcher(path, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["groups", clientId]);
        queryClient.invalidateQueries(["groupProfile"]);
        return queryClient.invalidateQueries(["archivedGroups"]);
      },
      mutationKey: ["unarchiveGroup"],
    },
  );
};

/**
 * Export group members data with Select All support
 */
export const exportGroupMembers = async (
  token: string,
  clientId: string,
  groupId: string,
  request: IGroupExportRequest,
  fileName = "export.csv",
) => {
  if (token) {
    const data: Blob = await secureFetcher(
      `${apiPathGroup}${clientId}/${groupId}/export`,
      token,
      {
        body: JSON.stringify(request),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
      undefined,
      true,
    );
    const url = window.URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

/**
 * Export group member attachments with Select All support
 */
export const useExportGroupAttachments = (groupId: string) => {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  return useMutation(
    (request: IGroupAttachmentsExportRequest) => {
      return secureFetcher(`${apiPathGroup}${clientId}/${groupId}/export/attachments`, token, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }) as Promise<ISuccessResponse>;
    },
    {
      onError: (error) => {
        console.error("Error exporting group attachments", error);
      },
      mutationKey: ["exportGroupAttachments", clientId, groupId],
    },
  );
};
